import React, { useEffect, useRef, useState } from "react";
import "./CreatePassword.scss";

import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ROUTES from "../../../../constants/routes";
import CustomButton from "../../../../components/CustomButton/CustomButton";
import { useForm } from "react-hook-form";
import InputField from "../../../../components/InputField/InputField";
import { IoIosArrowBack } from "react-icons/io";

import { registerUser } from "../../../../api/permissions";
import { toast } from "react-toastify";
import { updateUserPassword } from "../../../../api/userManagement";
import { updateOpenSubscriptionAd } from "../../../../redux/features/subscription-slice";
import Captcha from "../../../../components/Captcha/Captcha";
import { Capacitor } from "@capacitor/core";
import useUserIsAllowed from "../../../../helpers/userAuthorized";

const CreatePassword = ({ desktopView, reload, setSelectedDesktopTag }) => {
  const dispatch = useDispatch();
  const { id: organization_id } = useSelector(
    (state) => state.organizationReducer.value
  );

  const { id: branch_id } = useSelector((state) => state.branchReducer.value);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { errors },
  } = useForm({});

  const navigate = useNavigate();
  const location = useLocation();
  const addData = location.state?.addData || {};

  const userId = location.state?.userId || null;
  const userDetails = location.state?.userDetails || {};

  const [submitting, setSubmitting] = useState(false);

  const reRef = useRef(null);
  const userIsAllowedToAddEditUser = useUserIsAllowed("administrator");

  useEffect(() => {
    if (!userId) {
      if (!addData?.name || !addData?.email) {
        navigate(ROUTES.addUser.path, { state: { addData: addData } });
      }
    }
  }, [addData]);

  const handleSubmitUser = async (data) => {
    if (data?.password === data?.confirm_password) {
      if (`${data?.password}`.length < 8) {
        setError("confirm_password", {
          type: "custom",
          message: "*Atleast 8 characters!",
        });
      } else {
        setSubmitting(true);

        if (userId) {
          const userData = {
            password: data?.password,
            password_confirmation: data?.confirm_password,
          };

          updateUserPassword(userId, userData)
            .then((res) => {
              toast.dismiss(); toast.success("Password changed successfully.");
              if (desktopView) {
                setSelectedDesktopTag && setSelectedDesktopTag("User Details");
              } else {
                navigate(ROUTES.manageUserBranches.path, {
                  state: { userData: userDetails },
                });
              }
            })
            .catch((err) => {
              if (err?.response?.data?.message) {
                if (err?.response?.data?.message === "User not authorized!") {
                  toast.dismiss(); toast.error(
                    "You do not have sufficient permissions to perform this action. Please contact your administrator."
                  );
                } else {
                  if (err?.response?.data?.errors) {
                    if (err?.response?.data?.errors?.password) {
                      toast.dismiss(); toast.error(`${err?.response?.data?.errors?.password}`);
                    } else {
                      toast.dismiss(); toast.error(`${err?.response?.data?.message}`);
                    }
                  } else {
                    toast.dismiss(); toast.error(`${err?.response?.data?.message}`);
                  }
                }
              } else {
                toast.dismiss(); toast.error("Password change failed!");
              }
            })
            .finally(() => {
              setSubmitting(false);
            });
        } else {
          let captchaToken = "";
          let source = "";
          if (Capacitor.isNativePlatform()) {
            source = "mobile";
          } else {
            captchaToken = await reRef.current.executeAsync();
          }
          const formData = new FormData();

          formData.append("name", addData?.name);
          formData.append("email", addData?.email);
          formData.append("password", data?.password);
          formData.append("is_admin", 0);
          formData.append("status", addData?.status);
          formData.append("is_add_user", true);
          formData.append("organization_id", organization_id);
          formData.append("branch_id", branch_id);
          formData.append("captchaToken", captchaToken);
          formData.append("source", source);

          registerUser(formData)
            .then(async (res) => {
              desktopView && reload && reload(res.data?.data);
              toast.dismiss(); toast.success("User created successfully.");
              navigate(ROUTES.manageUser.path);
            })
            .catch((err) => {
              if (err?.response?.data?.message) {
                if (err?.response?.data?.message === "User not authorized!") {
                  toast.dismiss(); toast.error(
                    "You do not have sufficient permissions to perform this action. Please contact your administrator."
                  );
                } else {
                  if (err?.response?.data?.errors) {
                    if (err?.response?.data?.errors?.name) {
                      toast.dismiss(); toast.error(`${err?.response?.data?.errors?.name}`);
                    } else if (err?.response?.data?.errors?.email) {
                      toast.dismiss(); toast.error(`${err?.response?.data?.errors?.email}`);
                    } else if (err?.response?.data?.errors?.password) {
                      toast.dismiss(); toast.error(`${err?.response?.data?.errors?.password}`);
                    } else {
                      toast.dismiss(); toast.error(`${err?.response?.data?.message}`);
                    }
                  } else {
                    toast.dismiss(); toast.error(`${err?.response?.data?.message}`);
                  }
                }
              } else {
                toast.dismiss(); toast.error("Error while creating new user.");
              }
              if (err?.response?.data?.upgrade) {
                dispatch(updateOpenSubscriptionAd({ openAd: true }));
              }
            })
            .finally(() => {
              setSubmitting(false);
            });
        }
      }
    } else {
      setError("confirm_password", {
        type: "custom",
        message: "Both password does not match!",
      });
    }
  };
  return (
    <>
      <Captcha reRef={reRef} />
      <form
        onSubmit={handleSubmit(handleSubmitUser)}
        className="add-password container-wrapper"
      >
        <div className="input-container">
          <InputField
            name={"password"}
            type={"password"}
            label={userId ? "New Password" : "Password"}
            required={true}
            register={register}
            inputErrors={errors}
            watch={watch}
            constraints={{
              minLength: {
                value: 8,
                message: "Password must be at least 8 characters long",
              },
              maxLength: {
                value: 30,
                message: "Password must not be more than 30 characters",
              },
            }}
            disabled={!userIsAllowedToAddEditUser}
          />

          <InputField
            name={"confirm_password"}
            type={"password"}
            label={"Re-enter Password"}
            required={true}
            register={register}
            inputErrors={errors}
            watch={watch}
            constraints={{
              minLength: {
                value: 8,
                message: "Password must be at least 8 characters long",
              },
            }}
            disabled={!userIsAllowedToAddEditUser}
          />

          <div className="buttons">
            <CustomButton
              name={userId ? "Change Password" : "Create User"}
              color={"primary"}
              type={"submit"}
              handleSubmit={() => {}}
              disabled={submitting || !userIsAllowedToAddEditUser}
              bigButton={true}
              loader={submitting}
            />
          </div>
        </div>

        <div
          className="navigation-back-button"
          onClick={() => {
            if (userId) {
              navigate(ROUTES.manageUserBranches.path, {
                state: { userData: { id: userId } },
              });
            } else {
              navigate(ROUTES.addUser.path, {
                state: { addData: addData },
              });
            }
          }}
        >
          <IoIosArrowBack />
        </div>
      </form>
    </>
  );
};

export default CreatePassword;
